/* eslint-disable camelcase, eqeqeq */

import { is } from 'ramda';
import { of, concat, EMPTY } from 'rxjs';

import { sendAnalytics } from '@ezugi/analytics/sendAnalytics';
import {
  DISCONNECTED,
  SERVER_DISCONNECTED_INACTIVITY,
  DISCONNECTED_SESSION,
  BLOCKED_PLAYER,
  ERROR,
  TABLE_CANCELLED,
  TABLE_CLOSED,
  CANCEL_ROUND,
  FATAL_ERROR,
  NO_SPIN,
  OPERATOR_ERROR,
  BET_NOT_PLACED,
  TIP_NOT_PLACED,
  ERRORS,
} from '@ezugi/constants';

import {
  betActions,
  dialogActions,
  errorActions,
  notificationActions,
  roundActions,
  socketActions,
  tipsActions,
  videoActions,
} from '../../actions';
import { ERROR_SHOW, DISCONNECT_SHOW } from '../../constants/analytics';
import {
  DISCONNECTED_SESSION_DIALOG,
  DISCONNECTED_FOR_INACTIVITY_DIALOG,
  TABLE_CLOSED_DIALOG,
  DAILY_LIMIT_EXCEEDED_DIALOG,
  LOW_REAL_BALANCE_DIALOG,
  OPERATOR_ERROR_DIALOG,
  GEOLOCATION_ERROR_DIALOG,
} from '../../constants/dialog';
import { showInactivityDialogSelector } from '../../selectors';
import { retailModeSelector } from '../../selectors/app';
import { RootState } from '../../types';
import { getActions, getNoSpinAction } from './utils';

const {
  CODES,
  PLAYER_BLOCKED,
  FAILED_TO_CALL_GAME_ENGINE,
} = ERRORS;

const { round } = roundActions;
const { bet } = betActions;
const { dialog } = dialogActions;
const { notification } = notificationActions;
const { error } = errorActions;
const { socket } = socketActions;
const { video } = videoActions;
const { tips } = tipsActions;

const handleFatalError = () => of(
  socket.error({ error: FATAL_ERROR }),
  error.set({
    code: CODES[FAILED_TO_CALL_GAME_ENGINE],
    message: 'FAILED_TO_CALL_GAME_ENGINE',
  }),
  socket.close(),
);

const handleOperatorErrorFallback = () => of(
  bet.reset(),
  notification.add({ message: 'messages.bet_too_late' }),
);

const handleBetNotPlacedError = () => of(bet.reset(), notification.add({ message: 'messages.bet_not_placed' }));

const handleDisconnectedError = ({ MessageType, state }: { MessageType: string, state: RootState }) => {
  const showInactivityDialog = showInactivityDialogSelector(state);

  const dialogName = MessageType === SERVER_DISCONNECTED_INACTIVITY
    ? DISCONNECTED_FOR_INACTIVITY_DIALOG
    : DISCONNECTED_SESSION_DIALOG;

  sendAnalytics({
    event: DISCONNECT_SHOW,
    disconnect_type: MessageType,
  });

  return concat(
    of(socket.error({ error: MessageType })),
    of(video.stop()),
    of(round.set({ roundStatus: DISCONNECTED })),
    showInactivityDialog ? of(dialog.add({ name: dialogName })) : EMPTY,
    of(socket.close()),
  );
};

const handleBlockedPlayerError = () => of(
  socket.error({ error: BLOCKED_PLAYER }),
  error.set({ code: CODES[PLAYER_BLOCKED], message: 'PLAYER_BLOCKED' }),
  socket.close(),
);

const handleTableCanceledError = ({
  MessageType,
  Code,
  ErrorCode,
  MessageID,
}: {
  MessageType: string,
  Code: number,
  ErrorCode: number,
  MessageID: number,
}) => {
  sendAnalytics({
    event: ERROR_SHOW,
    error_name: MessageType || TABLE_CANCELLED,
    error_id: Code || ErrorCode || MessageID,
  });

  return concat(
    of(socket.error({ error: TABLE_CANCELLED })),
    of(video.stop()),
    of(bet.reset()),
    of(round.set({ roundStatus: TABLE_CLOSED })),
    of(dialog.add({ name: TABLE_CLOSED_DIALOG })),
    of(socket.close()),
  );
};

const handleCancelRoundError = ({ MessageType, Code, ErrorCode, MessageID }: {
  MessageType: string,
  Code: number,
  ErrorCode: number,
  MessageID: number,
}) => {
  sendAnalytics({
    event: ERROR_SHOW,
    error_name: MessageType || CANCEL_ROUND,
    error_id: Code || ErrorCode || MessageID,
  });

  return concat(
    of(socket.error({ error: CANCEL_ROUND })),
    of(bet.reset()),
  );
};

const handleNoSpinError = ({
  MessageType,
  Code,
  ErrorCode,
  MessageID,
  payload,
}: {
  MessageType: string,
  Code: number,
  ErrorCode: number,
  MessageID: number,
  payload: any,
}) => {
  sendAnalytics({
    event: ERROR_SHOW,
    error_name: MessageType || payload.NoSpinReason,
    error_id: Code || ErrorCode || MessageID,
  });

  return concat(
    of(socket.error({ error: NO_SPIN })),
    of(getNoSpinAction(payload)),
  );
};

const handleGenericError = (handleErrorFromGame?: Function) => ({ Code, ErrorCode, state, payload }: {
  Code: number,
  ErrorCode: number,
  state: RootState,
  payload: any,
}) => {
  const _errorAction = getActions(+Code || +ErrorCode, state);

  // PLEASE LOOK IN OTHER GAMES FOR config.handleError
  if (is(Function)(handleErrorFromGame)) {
    return concat(handleErrorFromGame({ Code, ErrorCode, payload }), of(_errorAction));
  }

  return concat(
    of(bet.reset()),
    of(socket.error({ error: ERROR })),
    of(_errorAction),
  );
};

const handleSocketErrors = (
  { MessageType, MessageID, Code, ErrorCode, requiredAction, ...payload }: {
    MessageType: string,
    MessageID: number,
    Code: number,
    ErrorCode: number,
    requiredAction: any,
    payload: any
  },
  state: RootState,
  { handleOperatorError = handleOperatorErrorFallback, handleError }: any
) => {
  const errorHandler: Record<string, any> = {
    [FATAL_ERROR]: handleFatalError,
    [OPERATOR_ERROR]: ({ payload: messagePayload }: { payload: any }) => handleOperatorError(messagePayload, state),
    [BET_NOT_PLACED]: handleBetNotPlacedError,
    [DISCONNECTED_SESSION]: handleDisconnectedError,
    [SERVER_DISCONNECTED_INACTIVITY]: handleDisconnectedError,
    [BLOCKED_PLAYER]: handleBlockedPlayerError,
    [TABLE_CANCELLED]: handleTableCanceledError,
    [CANCEL_ROUND]: handleCancelRoundError,
    [NO_SPIN]: handleNoSpinError,
    [ERROR]: handleGenericError(handleError),
  };

  if (MessageType === OPERATOR_ERROR && requiredAction != null) {
    return of(
      dialog.add({
        ...requiredAction,
        name: OPERATOR_ERROR_DIALOG,
      }),
    );
  }

  if (MessageType === TIP_NOT_PLACED) {
    return of(
      tips.failure(),
      notification.add({ message: 'notifications.tip_not_placed' }),
    );
  }

  if (retailModeSelector(state) && (ErrorCode == 4 || ErrorCode == 5 || ErrorCode == 11)) {
    return null;
  }

  if (ErrorCode == 4 || ErrorCode == 5 /* daily limit */) {
    return of(dialog.add({ name: DAILY_LIMIT_EXCEEDED_DIALOG }), bet.reset());
  }

  if (ErrorCode == 11 /* low real balance */) {
    return of(dialog.add({ name: LOW_REAL_BALANCE_DIALOG }));
  }

  /* handle other relevant operator errors */
  const maybeOperatorCode = [ 1, 3, 6, 7, 8 ].find((code) => ErrorCode == code);
  if (maybeOperatorCode) {
    return of(bet.reset(), notification.add({ message: `notifications.operator_error_code_${maybeOperatorCode}` }));
  }

  if (ErrorCode == 24 /* geolocation error */) {
    return of(dialog.add({ name: GEOLOCATION_ERROR_DIALOG }));
  }

  if (errorHandler[MessageType]) {
    return errorHandler[MessageType]({
      MessageType,
      Code,
      ErrorCode,
      MessageID,
      payload,
      state,
    });
  }

  return null;
};

export default handleSocketErrors;
